import {
  Children,
  isValidElement,
  ReactElement,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

export interface GroupedSelectProps {
  value?: string;
  defaultValue?: string;
  onChange?: (e: { target: { value: string } }) => void;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  id?: string;
  name?: string;
  placeholder?: string;
  children?: ReactNode;
}

interface ParsedOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

interface ParsedGroup {
  label: string | null;
  options: ParsedOption[];
}

interface OptionElementProps {
  value?: string | number;
  disabled?: boolean;
  children?: ReactNode;
}

interface OptgroupElementProps {
  label?: string;
  children?: ReactNode;
}

function parseOption(child: unknown): ParsedOption | null {
  if (!isValidElement(child) || child.type !== 'option') return null;
  const props = (child as ReactElement<OptionElementProps>).props;
  return {
    value: String(props.value ?? ''),
    label: props.children ?? '',
    disabled: props.disabled,
  };
}

function parseGroups(children: ReactNode): { groups: ParsedGroup[]; flatOptions: ParsedOption[] } {
  const groups: ParsedGroup[] = [];
  let bareGroup: ParsedGroup | null = null;
  const flatOptions: ParsedOption[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === 'optgroup') {
      const props = (child as ReactElement<OptgroupElementProps>).props;
      const innerOptions: ParsedOption[] = [];
      Children.forEach(props.children, (inner) => {
        const opt = parseOption(inner);
        if (opt) {
          innerOptions.push(opt);
          flatOptions.push(opt);
        }
      });
      if (innerOptions.length > 0) {
        groups.push({ label: props.label ?? null, options: innerOptions });
      }
    } else if (child.type === 'option') {
      const opt = parseOption(child);
      if (opt) {
        if (!bareGroup) {
          bareGroup = { label: null, options: [] };
        }
        bareGroup.options.push(opt);
        flatOptions.push(opt);
      }
    }
  });

  // Bare options trafiają na górę listy (najczęściej placeholder).
  if (bareGroup) groups.unshift(bareGroup);

  return { groups, flatOptions };
}

const TRIGGER_BASE =
  'w-full flex items-center justify-between gap-2 ' +
  'rounded-input bg-surface-base/60 text-sm text-left ' +
  'px-field-x py-field-y ' +
  'border transition-colors duration-fast ease-smooth ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary/40 focus-visible:border-accent-primary/50';

export function GroupedSelect({
  value,
  defaultValue,
  onChange,
  disabled,
  error,
  className,
  id,
  name,
  placeholder,
  children,
}: GroupedSelectProps) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<string>(defaultValue ?? '');
  const currentValue = isControlled ? value! : internalValue;

  const [open, setOpen] = useState(false);
  const [focusValue, setFocusValue] = useState<string | null>(null);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const { groups, flatOptions } = useMemo(() => parseGroups(children), [children]);
  const selectedOption = flatOptions.find((o) => o.value === currentValue);

  const triggerLabel: ReactNode = (() => {
    if (selectedOption && selectedOption.value !== '') return selectedOption.label;
    if (selectedOption && selectedOption.value === '') {
      return selectedOption.label || placeholder || '';
    }
    return placeholder || '';
  })();

  const isPlaceholderShown = !selectedOption || selectedOption.value === '';

  const handleSelect = (newValue: string) => {
    if (!isControlled) setInternalValue(newValue);
    onChange?.({ target: { value: newValue } });
    setOpen(false);
    triggerRef.current?.focus();
  };

  // Lista enabled values dla nawigacji klawiaturą.
  const enabledValues = useMemo(
    () => flatOptions.filter((o) => !o.disabled).map((o) => o.value),
    [flatOptions],
  );

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPopupPos({ top: rect.bottom + 6, left: rect.left, width: rect.width });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insidePopup = popupRef.current?.contains(target);
      if (!insideTrigger && !insidePopup) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (enabledValues.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusValue((prev) => {
          const cur = prev == null ? -1 : enabledValues.indexOf(prev);
          return enabledValues[(cur + 1) % enabledValues.length];
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusValue((prev) => {
          const cur = prev == null ? enabledValues.length : enabledValues.indexOf(prev);
          return enabledValues[(cur - 1 + enabledValues.length) % enabledValues.length];
        });
      } else if (e.key === 'Enter' && focusValue != null) {
        e.preventDefault();
        const opt = flatOptions.find((o) => o.value === focusValue);
        if (opt && !opt.disabled) handleSelect(opt.value);
      } else if (e.key === 'Tab') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, enabledValues, focusValue, flatOptions]);

  useEffect(() => {
    if (!open) {
      setFocusValue(null);
      return;
    }
    const found = flatOptions.find((o) => o.value === currentValue && !o.disabled);
    setFocusValue(found ? found.value : null);
  }, [open]);

  const triggerClasses = [
    TRIGGER_BASE,
    error ? 'border-danger/60 focus-visible:ring-danger/30 focus-visible:border-danger' : 'border-border hover:border-border-strong',
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    isPlaceholderShown ? 'text-text-muted' : 'text-text-primary',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        id={id}
        name={name}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((o) => !o);
        }}
        className={triggerClasses}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{triggerLabel || '\u00A0'}</span>
        <ChevronDown
          className={`w-4 h-4 text-text-muted shrink-0 transition-transform duration-fast ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && popupPos && createPortal(
        <div
          ref={popupRef}
          role="listbox"
          style={{ position: 'fixed', top: popupPos.top, left: popupPos.left, width: popupPos.width, zIndex: 11050 }}
          className="max-h-80 overflow-y-auto rounded-xl bg-surface-raised border border-border shadow-glass backdrop-blur-xl py-1.5 animate-scale-in origin-top"
        >
          {flatOptions.length === 0 && (
            <div className="px-3 py-2 text-sm text-text-muted">Brak opcji</div>
          )}
          {groups.map((group, gIdx) => (
            <div key={`group-${gIdx}-${group.label ?? 'bare'}`}>
              {gIdx > 0 && <div className="my-1 h-px bg-border-subtle" aria-hidden="true" />}
              {group.label && (
                <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
                  {group.label}
                </div>
              )}
              {group.options.map((opt, idx) => {
                const isSelected = opt.value === currentValue;
                const isFocused = focusValue === opt.value;
                return (
                  <button
                    key={opt.value === '' ? `__empty-${gIdx}-${idx}` : opt.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={opt.disabled}
                    onClick={() => {
                      if (!opt.disabled) handleSelect(opt.value);
                    }}
                    onMouseEnter={() => !opt.disabled && setFocusValue(opt.value)}
                    className={[
                      'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors',
                      opt.disabled
                        ? 'opacity-50 cursor-not-allowed text-text-disabled'
                        : isSelected
                          ? 'text-accent-primary'
                          : 'text-text-secondary hover:text-text-primary',
                      isFocused && !opt.disabled
                        ? isSelected
                          ? 'bg-accent-primary/10'
                          : 'bg-surface-hover text-text-primary'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <span className="truncate flex-1">{opt.label || '\u00A0'}</span>
                    {isSelected && (
                      <Check className="w-4 h-4 text-accent-primary shrink-0" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
